/**
 * An icon as plain vector outlines a native view can stroke: every shape of
 * the SVG turned into ABSOLUTE `M` / `L` / `C` / `Z` commands only.
 *
 * Why: the native glass bar (docs/liquid-glass-native-bar-plan.md) draws the
 * web's own lucide icons, and the fold circle can show any page's icon, so a
 * fixed set of bundled images cannot cover it. Android's `PathParser` reads
 * any SVG path, but UIKit has no SVG parser at all — so the normalising is
 * done once, here, and both native sides need to understand four letters.
 *
 * Directive-free and DOM-free at its core (`shapeToPath` takes plain
 * attributes), so it is unit-testable; `glyphFromSvg` is the thin DOM reader.
 */

export interface Glyph {
  /** Absolute M/L/C/Z path data on the icon's own viewBox. */
  paths: string[];
  /** The viewBox width and height (lucide is 24 × 24). */
  w: number;
  h: number;
  /** Filled rather than stroked — the stop square is. */
  fill?: boolean;
}

type Attrs = Record<string, string | undefined>;

const num = (v: string | undefined, d = 0) => {
  const n = v == null ? NaN : parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

const r3 = (n: number) => {
  const v = Math.round(n * 1000) / 1000;
  return Object.is(v, -0) ? '0' : String(v);
};

/** One SVG shape element to path data (not yet normalised). */
export function shapeToPath(tag: string, a: Attrs): string | null {
  switch (tag) {
    case 'path':
      return a.d ?? null;
    case 'line':
      return `M${num(a.x1)} ${num(a.y1)}L${num(a.x2)} ${num(a.y2)}`;
    case 'polyline':
    case 'polygon': {
      const pts = (a.points ?? '').trim().split(/[\s,]+/).map(Number).filter(Number.isFinite);
      if (pts.length < 4) return null;
      let d = `M${pts[0]} ${pts[1]}`;
      for (let i = 2; i + 1 < pts.length; i += 2) d += `L${pts[i]} ${pts[i + 1]}`;
      return tag === 'polygon' ? `${d}Z` : d;
    }
    case 'circle':
    case 'ellipse': {
      const cx = num(a.cx), cy = num(a.cy);
      const rx = tag === 'circle' ? num(a.r) : num(a.rx);
      const ry = tag === 'circle' ? num(a.r) : num(a.ry);
      if (rx <= 0 || ry <= 0) return null;
      return `M${cx - rx} ${cy}A${rx} ${ry} 0 1 0 ${cx + rx} ${cy}A${rx} ${ry} 0 1 0 ${cx - rx} ${cy}Z`;
    }
    case 'rect': {
      const x = num(a.x), y = num(a.y), w = num(a.width), h = num(a.height);
      if (w <= 0 || h <= 0) return null;
      let rx = a.rx != null ? num(a.rx) : num(a.ry);
      let ry = a.ry != null ? num(a.ry) : rx;
      rx = Math.min(rx, w / 2);
      ry = Math.min(ry, h / 2);
      if (rx <= 0 || ry <= 0) return `M${x} ${y}H${x + w}V${y + h}H${x}Z`;
      return (
        `M${x + rx} ${y}H${x + w - rx}A${rx} ${ry} 0 0 1 ${x + w} ${y + ry}` +
        `V${y + h - ry}A${rx} ${ry} 0 0 1 ${x + w - rx} ${y + h}` +
        `H${x + rx}A${rx} ${ry} 0 0 1 ${x} ${y + h - ry}` +
        `V${y + ry}A${rx} ${ry} 0 0 1 ${x + rx} ${y}Z`
      );
    }
    default:
      return null;
  }
}

const ARGS: Record<string, number> = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };

function tokenize(d: string): [string, number[]][] {
  const out: [string, number[]][] = [];
  const re = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d))) {
    const cmd = m[1];
    const lower = cmd.toLowerCase();
    // Arc flags may be written without separators ("a1 1 0 011 1"), so read
    // arc arguments one number at a time with single-digit flags.
    const nums: number[] = [];
    const numRe = /-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi;
    if (lower === 'a') {
      const src = m[2];
      let i = 0;
      let k = 0;
      while (i < src.length) {
        const ch = src[i];
        if (/[\s,]/.test(ch)) { i++; continue; }
        const slot = k % 7;
        if (slot === 3 || slot === 4) {
          nums.push(ch === '1' ? 1 : 0);
          i++; k++;
          continue;
        }
        numRe.lastIndex = i;
        const n = numRe.exec(src);
        if (!n || n.index !== i) break;
        nums.push(parseFloat(n[0]));
        i += n[0].length; k++;
      }
    } else {
      let n: RegExpExecArray | null;
      while ((n = numRe.exec(m[2]))) nums.push(parseFloat(n[0]));
    }
    const per = ARGS[lower];
    if (per === 0) { out.push([cmd, []]); continue; }
    if (nums.length === 0) continue;
    for (let i = 0; i + per <= nums.length; i += per) {
      // Extra pairs after a moveto are implicit linetos (SVG spec).
      const c = i > 0 && lower === 'm' ? (cmd === 'm' ? 'l' : 'L') : cmd;
      out.push([c, nums.slice(i, i + per)]);
    }
  }
  return out;
}

/** SVG spec F.6.5: an endpoint arc as cubic Béziers, each spanning ≤ 90°. */
function arcToCubics(
  x1: number, y1: number, rx: number, ry: number, phiDeg: number,
  large: number, sweep: number, x2: number, y2: number,
): number[][] {
  if (rx === 0 || ry === 0 || (x1 === x2 && y1 === y2)) return [[x1, y1, x2, y2, x2, y2]];
  rx = Math.abs(rx); ry = Math.abs(ry);
  const phi = (phiDeg * Math.PI) / 180;
  const cos = Math.cos(phi), sin = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy;
  const y1p = -sin * dx + cos * dy;
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) { const s = Math.sqrt(lambda); rx *= s; ry *= s; }
  const num2 = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  let coef = Math.sqrt(Math.max(0, num2 / den));
  if (large === sweep) coef = -coef;
  const cxp = (coef * rx * y1p) / ry;
  const cyp = (-coef * ry * x1p) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2;
  const cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const angle = (ux: number, uy: number, vx: number, vy: number) => {
    const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    return a;
  };
  const ux = (x1p - cxp) / rx, uy = (y1p - cyp) / ry;
  const vx = (-x1p - cxp) / rx, vy = (-y1p - cyp) / ry;
  const theta1 = angle(1, 0, ux, uy);
  let delta = angle(ux, uy, vx, vy);
  if (!sweep && delta > 0) delta -= 2 * Math.PI;
  if (sweep && delta < 0) delta += 2 * Math.PI;
  const segs = Math.max(1, Math.ceil(Math.abs(delta) / (Math.PI / 2) - 1e-9));
  const step = delta / segs;
  const k = (4 / 3) * Math.tan(step / 4);
  const out: number[][] = [];
  let t = theta1;
  const pt = (ang: number) => {
    const ex = rx * Math.cos(ang), ey = ry * Math.sin(ang);
    return [cos * ex - sin * ey + cx, sin * ex + cos * ey + cy];
  };
  const dpt = (ang: number) => {
    const ex = -rx * Math.sin(ang), ey = ry * Math.cos(ang);
    return [cos * ex - sin * ey, sin * ex + cos * ey];
  };
  for (let i = 0; i < segs; i++) {
    const t2 = t + step;
    const [ax, ay] = pt(t), [bx, by] = pt(t2);
    const [dax, day] = dpt(t), [dbx, dby] = dpt(t2);
    out.push([ax + k * dax, ay + k * day, bx - k * dbx, by - k * dby, bx, by]);
    t = t2;
  }
  // Land exactly on the endpoint the path asked for, not a rounding of it.
  out[out.length - 1][4] = x2;
  out[out.length - 1][5] = y2;
  return out;
}

/** Any SVG path data to absolute M / L / C / Z only. */
export function normalizePath(d: string): string {
  let x = 0, y = 0, sx = 0, sy = 0;
  let lastC: [number, number] | null = null; // last cubic control 2, for S
  let lastQ: [number, number] | null = null; // last quadratic control, for T
  const parts: string[] = [];
  const C = (c1x: number, c1y: number, c2x: number, c2y: number, ex: number, ey: number) =>
    parts.push(`C${r3(c1x)} ${r3(c1y)} ${r3(c2x)} ${r3(c2y)} ${r3(ex)} ${r3(ey)}`);
  for (const [cmd, a] of tokenize(d)) {
    const rel = cmd === cmd.toLowerCase() && cmd !== 'z';
    const ox = rel ? x : 0, oy = rel ? y : 0;
    const up = cmd.toUpperCase();
    let nextC: [number, number] | null = null;
    let nextQ: [number, number] | null = null;
    switch (up) {
      case 'M':
        x = ox + a[0]; y = oy + a[1]; sx = x; sy = y;
        parts.push(`M${r3(x)} ${r3(y)}`);
        break;
      case 'L':
        x = ox + a[0]; y = oy + a[1];
        parts.push(`L${r3(x)} ${r3(y)}`);
        break;
      case 'H':
        x = (rel ? x : 0) + a[0];
        parts.push(`L${r3(x)} ${r3(y)}`);
        break;
      case 'V':
        y = (rel ? y : 0) + a[0];
        parts.push(`L${r3(x)} ${r3(y)}`);
        break;
      case 'C': {
        const [c1x, c1y, c2x, c2y, ex, ey] = [ox + a[0], oy + a[1], ox + a[2], oy + a[3], ox + a[4], oy + a[5]];
        C(c1x, c1y, c2x, c2y, ex, ey);
        nextC = [c2x, c2y]; x = ex; y = ey;
        break;
      }
      case 'S': {
        const c1x = lastC ? 2 * x - lastC[0] : x, c1y = lastC ? 2 * y - lastC[1] : y;
        const [c2x, c2y, ex, ey] = [ox + a[0], oy + a[1], ox + a[2], oy + a[3]];
        C(c1x, c1y, c2x, c2y, ex, ey);
        nextC = [c2x, c2y]; x = ex; y = ey;
        break;
      }
      case 'Q':
      case 'T': {
        const prevQ: [number, number] | null = lastQ;
        const qx: number = up === 'Q' ? ox + a[0] : prevQ ? 2 * x - prevQ[0] : x;
        const qy: number = up === 'Q' ? oy + a[1] : prevQ ? 2 * y - prevQ[1] : y;
        const [ex, ey] = up === 'Q' ? [ox + a[2], oy + a[3]] : [ox + a[0], oy + a[1]];
        C(x + (2 / 3) * (qx - x), y + (2 / 3) * (qy - y), ex + (2 / 3) * (qx - ex), ey + (2 / 3) * (qy - ey), ex, ey);
        nextQ = [qx, qy]; x = ex; y = ey;
        break;
      }
      case 'A': {
        const [ex, ey] = [ox + a[5], oy + a[6]];
        for (const c of arcToCubics(x, y, a[0], a[1], a[2], a[3], a[4], ex, ey)) C(c[0], c[1], c[2], c[3], c[4], c[5]);
        x = ex; y = ey;
        break;
      }
      case 'Z':
        parts.push('Z');
        x = sx; y = sy;
        break;
    }
    lastC = nextC;
    lastQ = nextQ;
  }
  return parts.join('');
}

/** Read a rendered <svg> (a lucide icon already on the page) into a glyph. */
export function glyphFromSvg(svg: SVGSVGElement): Glyph {
  const vb = svg.viewBox?.baseVal;
  const w = vb && vb.width ? vb.width : 24;
  const h = vb && vb.height ? vb.height : 24;
  const paths: string[] = [];
  let fill = false;
  svg.querySelectorAll('path, line, polyline, polygon, circle, ellipse, rect').forEach((el) => {
    const attrs: Attrs = {};
    for (const at of Array.from(el.attributes)) attrs[at.name] = at.value;
    const d = shapeToPath(el.tagName.toLowerCase(), attrs);
    if (d) paths.push(normalizePath(d));
  });
  const f = svg.getAttribute('fill');
  if (f && f !== 'none') fill = true;
  return { paths, w, h, ...(fill ? { fill } : {}) };
}

/** Apply an affine transform (a b c d e f, as SVG's `matrix()`) to normalised data. */
export function transformPath(d: string, [a, b, c, dd, e, f]: [number, number, number, number, number, number]): string {
  return d.replace(/([MLC])([^MLCZ]*)/g, (_m, cmd: string, args: string) => {
    const n = args.trim().split(/\s+/).map(Number);
    const out: string[] = [];
    for (let i = 0; i + 1 < n.length; i += 2) {
      out.push(`${r3(a * n[i] + c * n[i + 1] + e)} ${r3(b * n[i] + dd * n[i + 1] + f)}`);
    }
    return cmd + out.join(' ');
  });
}
